import { type Accessor, type Component, For, type JSX, Match, Show, Switch } from 'solid-js'
import TableHeaderCell from './custom-cells/TableHeaderCell'
import TableNoResults from './TableNoResults'
import CheckboxCell from './custom-cells/CheckboxCell'
import { SkeletonTableRows } from './Skeleton'

export interface TableColumn {
  readonly id: string
  readonly header?: JSX.Element
  readonly withHint?: true
  readonly sortable?: true
  readonly includesImage?: true
  readonly class?: string
  readonly isHideable?: boolean
  /** Explicit translation key for the header label. Defaults to `ls_table_{tableId}_col_{id}`. */
  readonly labelKey?: string
}

export enum TableStatus {
  Init = 'init',
  Loaded = 'loaded',
  Loading = 'loading',
  NoData = 'no_data',
  NoSearchResults = 'no_search_results',
  Custom = 'custom'
}

export interface TableProps {
  readonly status: () => TableStatus
  readonly children?: JSX.Element | string
  readonly orderBy?: Accessor<string | undefined>
  readonly setOrderBy?: (id: string | undefined) => void
  readonly columns: TableColumn[]
  readonly tableId: string
  readonly isCustomNoDataMsg?: true
  readonly onSelectAll?: (value: boolean) => void
  readonly setSelectorRef?: (el: HTMLInputElement) => void
}

const Table: Component<TableProps> = (props) => {
  const setOrderBy = (id: string | undefined): void => {
    if (!props.setOrderBy || !props.orderBy) {
      return
    }

    if (!id) {
      props.setOrderBy(undefined)
      return
    }

    const currentOrderBy = props.orderBy()
    if (currentOrderBy) {
      const [currentId, status] = currentOrderBy.split(':')
      if (currentId === id) {
        if (status === 'desc') {
          props.setOrderBy(`${id}:asc`)
          return
        }
        props.setOrderBy(undefined)
        return
      }
    }

    props.setOrderBy(`${id}:desc`)
  }

  const getSortStatus = (id: string): 'asc' | 'desc' | undefined => {
    if (!props.orderBy?.()) {
      return undefined
    }

    const [activeId, status] = props.orderBy()!.split(':')
    if (activeId === id) {
      return status as 'asc' | 'desc'
    }

    return undefined
  }

  const onSetOrder = (): ((id: string | undefined) => void) => {
    if ([TableStatus.Loading, TableStatus.Init].includes(props.status())) {
      return () => {}
    }
    return setOrderBy
  }

  return (
    <table class="siroc-table table-fixed min-w-full divide-y divide-gray-300 bg-white">
      <thead>
        <tr class="text-left text-sm font-semibold">
          <For each={props.columns}>
            {(col) => (
              <Switch>
                <Match when={col.id === 'select' && props.onSelectAll}>
                  <th scope="col" class="py-3.5 px-3">
                    <CheckboxCell
                      onValueChange={(value) => props.onSelectAll?.(value)}
                      setSelectorRef={props.setSelectorRef}
                    />
                  </th>
                </Match>
                <Match when={col.id === 'image' && !col.includesImage}>
                  <th scope="col" class={`${props.onSelectAll ? 'px-3' : 'pl-4 pr-3 sm:pl-6'} py-3.5`} />
                </Match>
                <Match when={col.id !== 'image' && col.id !== 'select'}>
                  <th scope="col" class={`px-4 py-[14px] ${col.class} bg-gray-50`} data-column-id={col.id}>
                    <Show
                      when={col.header}
                      fallback={
                        <TableHeaderCell
                          id={col.id}
                          tableId={props.tableId}
                          status={getSortStatus(col.id)}
                          setOrderBy={onSetOrder()}
                          withHint={col.withHint}
                          sortable={col.sortable}
                          labelKey={col.labelKey}
                        />
                      }
                    >
                      {col.header}
                    </Show>
                  </th>
                </Match>
              </Switch>
            )}
          </For>
        </tr>
      </thead>
      <tbody
        class="relative divide-y divide-gray-200 text-gray-900"
        classList={{ 'animate-pulse': props.status() === TableStatus.Loading }}
      >
        <Switch>
          <Match when={[TableStatus.Loaded, TableStatus.Loading, TableStatus.Custom].includes(props.status())}>
            {props.children}
          </Match>
          <Match when={props.status() === TableStatus.Init}>
            <SkeletonTableRows columns={props.columns.length} rows={5} />
          </Match>
          <Match when={props.status() === TableStatus.NoSearchResults}>
            <td class="h-60">
              <div class="absolute left-0 w-full top-0 bottom-0 flex items-center justify-center">
                <TableNoResults type="no_search_results" id={props.tableId} />
              </div>
            </td>
          </Match>
          <Match when={props.status() === TableStatus.NoData}>
            <td class="h-60">
              <div class="absolute left-0 w-full top-0 bottom-0 flex items-center justify-center">
                <TableNoResults type="no_data" id={props.isCustomNoDataMsg ? props.tableId : undefined} />
              </div>
            </td>
          </Match>
        </Switch>
      </tbody>
    </table>
  )
}

export default Table
